import { useState } from 'react'
import { Link } from 'react-router'
import { Play, Square, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { paths } from '@/routes/paths'
import { useTeamDay } from '@/features/office/api'
import { elapsedMs, formatClock } from './clock'
import { useActiveWork, useMyOpenItems, useStartWork, useTick } from './api'
import { StopWorkPopover } from './StopWorkPopover'

const QUIET_KEY = 'tw.workbar.quietUntil'
const QUIET_MS = 60 * 60 * 1000

const quietUntil = (): number => {
  try {
    return Number(window.localStorage.getItem(QUIET_KEY)) || 0
  } catch {
    return 0
  }
}

/**
 * Always in view, on every page. Timing something: what, for how long, and Stop. Timing nothing:
 * the items you are on, each one click from Start. It can be put away for an hour, but it does not
 * stay away — the point is that "what am I working on" is never left unanswered.
 */
export function StickyWorkBar({ workspaceSlug, enabled }: { workspaceSlug: string; enabled: boolean }) {
  const active = useActiveWork(enabled)
  const data = active.data
  const running = Boolean(data?.session && !data.session.endedAt)
  const mine = useMyOpenItems(enabled && active.isSuccess && !running)
  const team = useTeamDay(enabled)
  const start = useStartWork()
  const now = useTick(running)
  const [quiet, setQuiet] = useState(() => quietUntil() > Date.now())

  const others = (team.data?.people ?? []).filter((p) => p.working && p.userId !== team.data?.me)

  if (!enabled || !active.isSuccess) return null

  if (running && data?.session) {
    const label = data.card?.code ?? data.card?.title ?? 'Work item'
    return (
      <div className="pointer-events-none fixed inset-x-3 bottom-4 z-40 flex justify-center" role="region" aria-label="Work in progress">
        <div className="pointer-events-auto flex max-w-full items-center gap-3 rounded-full border border-success/50 bg-card py-1.5 pr-1.5 pl-4 shadow-lg">
          <span className="relative flex size-2.5 shrink-0" aria-hidden>
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-ios-green opacity-60 motion-reduce:animate-none" />
            <span className="relative inline-flex size-2.5 rounded-full bg-ios-green" />
          </span>
          <Link to={data.card ? `${paths.work(workspaceSlug)}?card=${data.card.id}` : paths.work(workspaceSlug)} className="min-w-0 truncate text-sm font-medium hover:underline" title={data.card?.title}>
            <span className="text-text-muted">Working on </span>
            {label}
            {data.card?.code ? <span className="hidden text-text-secondary sm:inline"> · {data.card.title}</span> : null}
          </Link>
          <span className="num shrink-0 text-sm font-semibold tabular-nums" role="timer" aria-label="Time on the item you are working on">
            {formatClock(elapsedMs(data.session.startedAt, data.now, data.fetchedAt, now))}
          </span>
          {others.length ? (
            <Link to={paths.teamToday(workspaceSlug)} className="hidden shrink-0 text-xs text-text-muted hover:underline md:inline" title={others.map((p) => p.displayName).join(', ')}>
              +{others.length} working
            </Link>
          ) : null}
          <StopWorkPopover>
            <Button type="button" size="sm" variant="destructive" className="shrink-0 rounded-full">
              <Square className="size-3" fill="currentColor" /> Stop
            </Button>
          </StopWorkPopover>
        </div>
      </div>
    )
  }

  const items = mine.data ?? []
  if (quiet || items.length === 0) return null
  return (
    <div className="pointer-events-none fixed inset-x-3 bottom-4 z-40 flex justify-center" role="region" aria-label="Start work">
      <div className="pointer-events-auto flex max-w-full flex-wrap items-center gap-2 rounded-2xl border border-brand/40 bg-card py-1.5 pr-1.5 pl-4 shadow-lg">
        <span className="text-sm font-semibold">What are you working on?</span>
        {items.map((c) => (
          <Button key={c.id} type="button" size="sm" className="max-w-52 rounded-full" disabled={start.isPending} onClick={() => start.mutate(c.id)} title={c.title}>
            <Play className="size-3 shrink-0" fill="currentColor" />
            <span className="truncate">{c.code ?? c.title}</span>
          </Button>
        ))}
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="size-8 rounded-full"
          aria-label="Hide for an hour"
          title="Hide for an hour"
          onClick={() => {
            try {
              window.localStorage.setItem(QUIET_KEY, String(Date.now() + QUIET_MS))
            } catch {
              /* hiding just lasts until the page reloads */
            }
            setQuiet(true)
          }}
        >
          <X className="size-4" />
        </Button>
      </div>
    </div>
  )
}
